import { useState, useCallback, useMemo } from 'react';
import { AxiosInstance } from 'axios';

import useApiRequest from 'hooks/useApiRequest';
import useAsync from 'hooks/useAsync';
import { Venue } from 'hooks/useVenue';
import {
  getParams,
  Options as VenueOptions,
  VenueSearchData,
  fixData as fixVenueData,
  paginateData,
} from 'hooks/useVenueSearch';
import { maxBy } from 'lodash';
import { getDiscounts, Discount } from 'hooks/usePropertyDiscount';
import { sortFields } from 'constants/sortFields';

import { filteredVenueDiscounts, filteredSpaceDiscounts } from 'utils/filters';

type LatLng = Venue['latLng'];

export interface Filters {
  latLng?: LatLng;
  capacity?: number;
  date?: string;
  distance?: number;
  facets?: string[];
  location?: string;
  property?: string[];
  roomTypes?: string[];
  sort?: keyof typeof sortFields;
  selectedVenueIds?: number[];
  dir?: 'asc' | 'desc';
}

type Options = VenueOptions<Filters>;

interface Aggregate<Name = string> {
  name: Name;
  count: number;
}

export interface Aggregates {
  property?: Aggregate[];
  distance?: Aggregate[];
  capacity?: Aggregate<number>[];
  facets?: Aggregate[];
}

export interface SpaceSearchData<V extends Venue = Venue>
  extends VenueSearchData<V> {
  totalSpaces: number;
  unfilteredTotalSpaces: number;
  aggregates: Aggregates;
}

export const getSearchData = (
  venues: Venue[] = [],
  totalVenues: number = venues.length,
  totalSpaces: number = venues.reduce(
    (prev, { spaces = [] }) => prev + spaces.length,
    0
  )
): SpaceSearchData => ({
  venues,
  alternativeVenues: [],
  totalVenues,
  totalSpaces,
  unfilteredTotalVenues: totalVenues,
  unfilteredTotalSpaces: totalSpaces,
  aggregates: { property: [], distance: [] },
});

export const emptyData = getSearchData();

const fixData = <Data extends SpaceSearchData = SpaceSearchData>(
  data: Data
): Data => ({
  ...fixVenueData(data),
  aggregates: {
    ...emptyData.aggregates,
    ...data.aggregates,
    ...('property' in data.aggregates
      ? {
          property: [...data.aggregates.property].sort((a, b) =>
            a.name.localeCompare(b.name)
          ),
        }
      : {}),
  },
});

const applyDiscounts = (
  data: SpaceSearchData,
  discounts: Discount[]
): SpaceSearchData => ({
  ...data,
  venues: data.venues.map(venue => ({
    ...venue,
    discount: maxBy(
      filteredVenueDiscounts(discounts, venue),
      discount => discount.amount
    ),
    discounts: filteredVenueDiscounts(discounts, venue),
    spaces: venue.spaces?.map(space => ({
      ...space,
      discount: maxBy(
        filteredSpaceDiscounts(discounts, venue, space),
        discount => discount.amount
      ),
      discounts: filteredSpaceDiscounts(discounts, venue, space),
    })),
  })),
});

export const getSpaceSearch = async (
  api: AxiosInstance,
  options: Options = {}
) => {
  const { data } = await api.get<SpaceSearchData>('/v1/spaces/search', {
    params: getParams(options),
  });
  const venueIds = [data.venues, data.alternativeVenues]
    .flat()
    .map(({ id }) => id);
  const spaceIds = [data.venues, data.alternativeVenues]
    .flat()
    .flatMap(venue => (venue?.spaces || []).map(space => space.id));

  const discounts =
    options.viewType === 'map'
      ? []
      : await getDiscounts(api, { venueIds, spaceIds });

  return applyDiscounts(fixData(data), discounts);
};

const useSpaceSearch = (options: Options = {}) => {
  const [data, setData] = useState<SpaceSearchData | undefined>();

  const apiRequest = useApiRequest();

  const { page = 1, filters: { selectedVenueIds } = {} } = options;

  const requestValid =
    selectedVenueIds === undefined || selectedVenueIds.length > 0;

  const optionsJson = useMemo(() => JSON.stringify(options), [options]);

  const clearData = useCallback(async () => {
    setData(emptyData);
  }, []);

  const fetchData = useCallback(async () => {
    const nextData = await getSpaceSearch(apiRequest, JSON.parse(optionsJson));

    setData(paginateData(nextData, page));
  }, [page, apiRequest, optionsJson]);

  const { loading, error, refresh } = useAsync(
    requestValid ? fetchData : clearData,
    requestValid
  );

  return { data, loading, error, refresh };
};

export default useSpaceSearch;
